import { toPng } from 'html-to-image';

/**
 * Marks a DOM node as a discrete block to place in the exported PDF (one widget card,
 * the tiles strip, etc.). Set on elements inside the dashboard export root, and read
 * back here when collecting the sections to capture.
 */
export const EXPORT_SECTION_ATTRIBUTE = 'data-export-section';

/**
 * Marks a node that indicates a widget is still loading (a spinner / "Loading" placeholder).
 * The exporter waits for all of these to clear so the PDF never captures a mid-load state.
 */
export const EXPORT_LOADING_ATTRIBUTE = 'data-export-loading';

/**
 * Added to the export root only while the capture runs. html-to-image clones the live DOM,
 * so styles scoped to this class reach the clone but never affect the on-screen dashboard;
 * it carries the capture-only fixes (see `.f-dashboard-exporting` in custom-elements.less).
 */
export const EXPORT_MODE_CLASS = 'f-dashboard-exporting';

/**
 * Marks a node to hide from the exported PDF (interactive controls like an "open in new tab"
 * button that are meaningless in a static report and, when captured, can reflow/clip the
 * layout). Hidden via `.f-dashboard-exporting [data-export-hide]` so it only affects the
 * capture clone, never the on-screen dashboard.
 */
export const EXPORT_HIDE_ATTRIBUTE = 'data-export-hide';

/**
 * A single captured dashboard block: a high-DPI PNG plus its on-screen geometry relative
 * to the export root. The geometry lets the PDF reproduce the dashboard grid (two-column
 * widgets, full-width strips) at the correct relative proportions instead of stretching
 * every block to the full page width.
 */
export interface ExportSection {
    dataUrl: string;
    /**
     * Grid-slot position/width in CSS pixels, relative to the export root's top-left. Values
     * can be slightly negative / exceed the root width because antd grid gutters apply
     * negative margins; the exporter normalises against the real min-left / max-right span.
     */
    left: number;
    top: number;
    width: number;
    /**
     * Intrinsic pixel size of the captured PNG. Uses the block's full scroll size so content
     * that overflows a card on screen (wide tables, no-wrap tag strips) is captured whole
     * rather than clipped; the exporter derives the PDF aspect ratio from these.
     */
    captureWidth: number;
    captureHeight: number;
    /**
     * Border-box of the actual card inside the section (the grid gutter sits between the
     * section edge and the card), in CSS pixels relative to the section's own top-left.
     * The exporter re-strokes this as a crisp vector rectangle: the card's thin, near-white
     * border is otherwise lost when the high-DPI capture is downscaled into the PDF.
     */
    cardLeft: number;
    cardTop: number;
    cardWidth: number;
    cardHeight: number;
}

/**
 * Higher than 1 so charts and text stay crisp when scaled into the PDF; 2x balances
 * sharpness against the data-URL size for a full dashboard of widgets.
 */
const CAPTURE_PIXEL_RATIO = 2;

// How long to wait for widgets to finish loading before capturing (best-effort cap).
const READY_TIMEOUT_MS = 20000;
// A short settle after loading clears so chart entry animations reach their final frame.
const SETTLE_MS = 600;

const delay = (ms: number): Promise<void> =>
    new Promise((resolve) => {
        window.setTimeout(resolve, ms);
    });

/**
 * Resolves once no [data-export-loading] markers remain inside the root, or the timeout is
 * hit (best-effort: a widget stuck loading should not block the export forever).
 *
 * Uses a MutationObserver rather than polling so the export fires the instant the last
 * marker clears (no fixed poll interval latency). The observer watches child structure and
 * the loading attribute across the whole subtree; a re-check is run immediately after
 * observe() to close the race where every marker clears between the initial check and the
 * observer being attached (in which case no further mutation would ever wake the observer).
 */
export async function waitForDashboardReady(root: HTMLElement): Promise<void> {
    const isReady = () => !root.querySelector(`[${EXPORT_LOADING_ATTRIBUTE}]`);

    if (!isReady()) {
        await new Promise<void>((resolve) => {
            let observer: MutationObserver;
            let timeoutId: ReturnType<typeof setTimeout>;

            const cleanup = () => {
                observer.disconnect();
                clearTimeout(timeoutId);
                resolve();
            };

            observer = new MutationObserver(() => {
                if (isReady()) {
                    cleanup();
                }
            });

            // Best-effort cap so a widget stuck loading never blocks the export forever.
            timeoutId = setTimeout(cleanup, READY_TIMEOUT_MS);

            observer.observe(root, {
                childList: true,
                subtree: true,
                attributes: true,
                attributeFilter: [EXPORT_LOADING_ATTRIBUTE],
            });

            // Guard against the markers clearing between the initial check and observe().
            if (isReady()) {
                cleanup();
            }
        });
    }

    await delay(SETTLE_MS);
}

/**
 * Captures every [data-export-section] inside the root as a high-resolution PNG, in DOM
 * order, recording each block's geometry relative to the root. Captures run sequentially
 * (reduce over a promise chain) because html-to-image mutates a detached clone of the
 * document and concurrent runs can interfere with each other; zero-size nodes (e.g. a
 * collapsed/hidden widget) are skipped, and a single block that fails to capture (e.g. a
 * tainted cross-origin avatar) is skipped rather than aborting the whole report.
 */
export function collectDashboardSections(root: HTMLElement): Promise<ExportSection[]> {
    const rootRect = root.getBoundingClientRect();
    const nodes = Array.from(root.querySelectorAll<HTMLElement>(`[${EXPORT_SECTION_ATTRIBUTE}]`));

    return nodes.reduce<Promise<ExportSection[]>>(async (accumulatorPromise, node) => {
        const collected = await accumulatorPromise;
        const rect = node.getBoundingClientRect();

        if (rect.width > 0 && rect.height > 0) {
            try {
                /**
                 * Capture the full content (scroll size), not just the visible box, so nothing
                 * that overflows the card horizontally/vertically is clipped in the export.
                 */
                const captureWidth = Math.max(node.scrollWidth, Math.round(rect.width));
                const captureHeight = Math.max(node.scrollHeight, Math.round(rect.height));
                const dataUrl = await toPng(node, {
                    pixelRatio: CAPTURE_PIXEL_RATIO,
                    backgroundColor: '#ffffff',
                    width: captureWidth,
                    height: captureHeight,
                });
                /**
                 * The section's first element child is the card itself (the grid gutter is the
                 * section's padding around it); fall back to the section box if there is none.
                 */
                const cardEl = (node.firstElementChild as HTMLElement | null) ?? node;
                const cardRect = cardEl.getBoundingClientRect();
                collected.push({
                    dataUrl,
                    left: rect.left - rootRect.left,
                    top: rect.top - rootRect.top,
                    width: rect.width,
                    captureWidth,
                    captureHeight,
                    cardLeft: cardRect.left - rect.left,
                    cardTop: cardRect.top - rect.top,
                    cardWidth: cardRect.width,
                    cardHeight: cardRect.height,
                });
            } catch (error) {
                // eslint-disable-next-line no-console
                console.error('Skipping a dashboard section that could not be captured for export:', error);
            }
        }

        return collected;
    }, Promise.resolve([]));
}
